import { createApp } from 'vue';
import App from './App.vue';
import StylePlugin from './utils/StylePlugin';
import router, { stripVuePrefix, withVuePrefix } from './utils/Routing';
import { vTooltip } from './utils/TooltipDirective';
import { findAppIdFromRoute } from './utils/App';

/**
 * Initialize the Vue application
 */
async function initializeApp() {
  try {
    // Initialize routing mode FIRST, before creating Vue app
    // This ensures the routing mode is set before any components mount
    // Stripped of the '/vue' server-mount prefix (this app now serves under '/vue' - see
    // plans/wobbly-napping-valiant.md) - `findAppIdFromRoute`/`main_path` matching stays in the
    // SAME un-prefixed shape it always has (see stripVuePrefix's own comment in Routing.ts).
    const currentUrl = window.location.origin + stripVuePrefix(window.location.pathname) + window.location.search + window.location.hash;
    console.log('[main] Initializing routing mode for URL:', currentUrl);

    try {
      const result = await findAppIdFromRoute(currentUrl, true);
      console.log('[main] Routing mode initialized');

      // Check if the URL needs to be collapsed (e.g., /apps/cds -> / in domain mode)
      if (result && result.isDomainMode && result.application) {
        const currentPath = stripVuePrefix(window.location.pathname);
        const appsPattern = /^\/apps\/([^\/]+)/;
        const match = currentPath.match(appsPattern);

        if (match) {
          const appName = match[1];
          const appMainPath = result.application.main_path?.replace(/^\/apps\//, '').replace(/^\//, '') || '';

          // If /apps/[appName] matches the current app in domain mode, collapse to root
          if (appName.toLowerCase() === appMainPath.toLowerCase()) {
            console.log(`[main] Collapsing /apps/${appName} to / in domain mode`);
            // Get the remaining path after /apps/[appName]
            const remainingPath = currentPath.substring(`/apps/${appName}`.length) || '/';
            const newUrl = window.location.origin + withVuePrefix(remainingPath) + window.location.search + window.location.hash;
            console.log(`[main] Redirecting to: ${newUrl}`);
            window.location.replace(newUrl);
            return; // Stop initialization, let the redirect happen
          }
        }
      }
    } catch (error) {
      console.error('[main] Error initializing routing mode:', error);
    }
    
    // Create Vue app instance
    const app = createApp(App);
    
    // Register global directive
    app.directive('tooltip', vTooltip);
    
    // Apply the StylePlugin to automatically handle component styles
    app.use(StylePlugin);

    // Use router before mounting
    app.use(router);
    
    // Mount the app
    app.mount('#app');
    
    console.log('Vue app initialized with router and tooltip directive');
    
  } catch (error) {
    console.error("Error initializing Vue app:", error);
    document.body.innerHTML = `
      <div style="color: red; padding: 20px;">
        <h1>Error loading Vue components</h1>
        <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
        <pre>${error instanceof Error ? error.stack : ''}</pre>
        <p>Make sure the server is running and components are built.</p>
      </div>
    `;
  }
}

// Initialize once the DOM is ready
document.addEventListener('DOMContentLoaded', initializeApp);